import { useState, useEffect } from 'react';
import { IonContent, IonPage, IonToast } from '@ionic/react';
import { base64FromPath } from '@ionic/react-hooks/filesystem';
import { Camera } from '@capacitor/camera';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { UserState, userStore } from '../api/AuthService';
import { Claim } from '../api/model/Claim';
import {
  buildClaimPhotoFilename,
  getClaimDisplayFields,
  loadClaimsPageData,
  postClaimPhotoRecords,
  sendClaimPhotoEmails,
} from '../utils/ClaimUtils';
import { toastStore } from '../components/AppToast';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { CameraIcon, TrashIcon } from '../components/icons';

interface Photo {
  filepath: string;
  webviewPath?: string;
  data: any;
}

const PHOTO_REQUIREMENTS = [
  'Photo of all sides of the truck',
  'Photo of the damage(s)',
  "Photo of the truck's VIN# on the truck/door (Identifies the truck)",
  "Photo of the truck's Mileage.",
];

const Claims: React.FC<{ moduleHistory: string }> = ({ moduleHistory }) => {
  const history = useHistory();
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();
  const [mounted, setMounted] = useState(false);

  const { driver } = userStore((state: UserState) => ({ driver: state.driver }));

  const [mostRecentClaim, setMostRecentClaim] = useState<Claim | null>(null);
  const [relatedClaimNumbers, setRelatedClaimNumbers] = useState<string[]>([]);
  const [relatedClaimIds, setRelatedClaimIds] = useState<number[]>([]);
  const [photoSubmissionRequired, setPhotoSubmissionRequired] = useState(false);
  const claimDisplayFields = getClaimDisplayFields(mostRecentClaim);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState<Photo[]>([]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!driver?.unit) {
      return;
    }

    loadClaimsPageData('claims', driver.unit, driver.driverCode)
      .then(({ claimGroup, photoSubmissionRequired: submissionRequired }) => {
        setMostRecentClaim(claimGroup.mostRecentClaim);
        setRelatedClaimNumbers(claimGroup.relatedClaimNumbers);
        setRelatedClaimIds(claimGroup.relatedClaimIds);
        setPhotoSubmissionRequired(submissionRequired);
      })
      .catch((error) => {
        console.log('[ClaimsPhoto] Claims page load error', error);
        setMostRecentClaim(null);
        setRelatedClaimNumbers([]);
        setRelatedClaimIds([]);
        setPhotoSubmissionRequired(false);
      });
  }, [driver?.unit, driver?.driverCode]);

  enum CameraResultType { Uri = 'uri', Base64 = 'base64', DataUrl = 'dataUrl' }

  async function submitClaimPhotos() {
    if (images.length < 1 || relatedClaimIds.length < 1 || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const claimNumbersForEmail = relatedClaimNumbers.length
        ? relatedClaimNumbers
        : relatedClaimIds.map(String);

      console.log('[ClaimsPhoto] submitClaimPhotos start', {
        claimNumbersForEmail,
        relatedClaimIds,
        photoCount: images.length,
      });

      await sendClaimPhotoEmails({
        relatedClaimNumbers: claimNumbersForEmail,
        photos: images,
        driver: {
          name: driver?.name,
          driverCode: driver?.driverCode,
          unit: driver?.unit,
          trailer: driver?.trailer,
          division: driver?.division,
          dmName: driver?.dmName,
        },
        incidentTime: mostRecentClaim?.incidentTime,
      });

      await postClaimPhotoRecords(
        'claims',
        relatedClaimIds,
        images.length,
        driver?.driverCode,
        driver?.unit,
      );

      console.log('[ClaimsPhoto] submitClaimPhotos complete');
      setSubmitSuccess(true);
      setImages([]);
      setPhotoSubmissionRequired(false);
    } catch (err: any) {
      console.log('[ClaimsPhoto] submitClaimPhotos error', err);
      const message = typeof err === 'string'
        ? err
        : err?.errmsg ?? err?.message ?? 'Failed to submit claim photos.';
      toastStore.getState().setToast(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  function getClaimNumberForFilename(): string {
    return String(relatedClaimNumbers[0] ?? mostRecentClaim?.claimNumber ?? 'claim');
  }

  async function takePicture() {
    const image = await Camera.getPhoto({ quality: 100, allowEditing: false, resultType: CameraResultType.Uri });
    const base64Data = await base64FromPath(image.webPath!);
    const claimNumber = getClaimNumberForFilename();
    setImages(prev => {
      const fileName = buildClaimPhotoFilename(claimNumber, prev.length + 1);
      return [...prev, { filepath: fileName, webviewPath: image.webPath, data: base64Data }];
    });
  }

  const removePhoto = (photo: Photo) => {
    const claimNumber = getClaimNumberForFilename();
    setImages(prev => prev
      .filter(img => img.filepath !== photo.filepath)
      .map((img, index) => ({
        ...img,
        filepath: buildClaimPhotoFilename(claimNumber, index + 1),
      })));
  };

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.replace('/docs');
  };

  return (
    <IonPage>
      <LoadDetailHeader title="Claims" onBack={goBack} onSheet={onSheet} />

      <IonToast
        isOpen={submitSuccess}
        onDidDismiss={() => setSubmitSuccess(false)}
        message="Your claim photos were submitted successfully."
        color="success"
        duration={3000}
      />

      <IonContent
        fullscreen
        className="design-a-content design-a-content--light"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
          <div className="home-hero photo-hero">
            <div className="photo-hero__eyebrow">Docs &amp; Resources</div>
            <div className="photo-hero__title home-display">Submit Claim Photos</div>
            <div className="photo-hero__meta">
              {images.length === 0 ? 'Photos required' : `${images.length} photo${images.length === 1 ? '' : 's'} ready to submit`}
              {driver?.unit ? ` · Unit ${driver.unit}` : ''}
            </div>
          </div>

          <div className="home-sheet photo-sheet">
            {photoSubmissionRequired ? (
              <div className="accident-alert">
                <strong>Action needed</strong>
                <p>You need to submit claim photos.</p>
              </div>
            ) : null}

            <div className="photo-card">
              <div className="photo-card__title">Claim details</div>
              <div className="inspection-list">
                <div className="inspection-row">
                  <span className="inspection-row__label">Unit #</span>
                  <span className="advance-card__body">{driver?.unit || '—'}</span>
                </div>
                {claimDisplayFields.map(({ key, label, value }) => (
                  <div className="inspection-row" key={key}>
                    <span className="inspection-row__label">{label}</span>
                    <span className="advance-card__body">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="photo-card">
              <div className="photo-card__title">Photo requirements</div>
              <ul className="accident-step__list" id="requiredPhotosSection">
                {PHOTO_REQUIREMENTS.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="photo-card">
              <div className="photo-card__title">Your photos</div>
              {images.length === 0 ? (
                <p className="photo-empty">No photos yet. Capture the required views.</p>
              ) : (
                <div className="photo-grid">
                  {images.map((img, index) => (
                    <button
                      key={img.filepath || index}
                      type="button"
                      className="photo-thumb"
                      onClick={() => removePhoto(img)}
                      aria-label="Remove photo"
                    >
                      <img alt="Claim photo" src={img.webviewPath} />
                      <span className="photo-thumb__remove">
                        <TrashIcon size={14} />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div id="ClaimsBottom" className="photo-actions">
              <button type="button" className="photo-capture" onClick={() => takePicture()}>
                <CameraIcon size={20} />
                <span>{images.length < 1 ? 'Take photo' : 'Take another photo'}</span>
              </button>
              <button
                type="button"
                className="profile-submit"
                disabled={images.length < 1 || relatedClaimIds.length < 1 || isSubmitting}
                onClick={() => submitClaimPhotos()}
              >
                Submit claim photos
              </button>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Claims;
